import { useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps, outputType } from 'types/hooks';
import { errorMessage } from 'config/constants';
import { portfolioAnalysisV2Parser } from 'parser/portfolioAnalysisV2Parser';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';

interface useFetchPortfolioAnalysisDataV2Props extends HookInputProps {
    kpiConfig: any[];
}
function useFetchPortfolioAnalysisDataV2({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
    kpiConfig,
}: useFetchPortfolioAnalysisDataV2Props) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<null | any[]>(null);
    const [count, setCount] = useState<number>(0);

    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setData(null);
            }
            setError(null);
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error: any) {
                onFetchError(error?.response?.data?.detail || error);
            }
        },
        ...queryConfig,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) {
                setError(null);
            }
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            if (payload && Array.isArray(payload?.data)) {
                let mergedData: any[] = [];
                const parsedData: any[] = portfolioAnalysisV2Parser(payload?.data, commonConfig, kpiConfig);
                //not coming from infinite scroll
                if (variables?.offset > 0 && data && data?.length > 0) {
                    mergedData = [...(data ?? []), ...parsedData];
                } else {
                    mergedData = parsedData;
                }
                setData(mergedData);
                setCount(payload?.total_count ?? 0);
            }
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setLoading(false);
        setError(error);
    }

    interface portfolioAnalysisOutputType extends outputType {
        onFetchError: Function;
    }
    const outputData: portfolioAnalysisOutputType = { onFetchError, error, loading, data, count };
    return outputData;
}

export { useFetchPortfolioAnalysisDataV2 };
